import {
  Box,
  Cards,
  ColumnLayout,
  Container,
  ExpandableSection,
  Grid,
  Header,
  Icon,
  Link,
  SpaceBetween,
  Spinner,
  Alert,
} from '@cloudscape-design/components';
import { useControls } from 'src/api/hooks/useControlData';
import { ControlMetadata } from '@amzn/controllershiphub-typescript-client';
import PageLayout from '../common/PageLayout/PageLayout';
import { PageHeader } from '../layout/PageHeader';
import './LandingPage.scss';
import { IconProps } from '@cloudscape-design/components/icon';
import { useNavigate } from 'react-router-dom';

interface StaticItem {
  title: string;
  content: string;
  icon: IconProps.Name;
}

interface SectionConfig {
  headerText: string;
  controlType?: string;
  className: string;
}

const CAPABILITIES: StaticItem[] = [
  {
    title: 'Centralization',
    content:
      'Controllership Hub is a single place to go to assist finance teams in controllership activities.',
    icon: 'unlocked',
  },
  {
    title: 'Unification',
    content:
      'Each new controllership automation is introduced as a new capability of a Controllership Hub, instead of creating a new tool.',
    icon: 'settings',
  },
  {
    title: 'Visibility',
    content:
      "Controllership Hub has persona-based dashboards for holistic monitoring of the control environment's health to validating that the underlying process are working as intended or whether they require improvement.",
    icon: 'search',
  },
];

const SECTION_CONFIG: SectionConfig[] = [
  {
    headerText: 'GRC Control Automation',
    controlType: 'FIXED_ASSETS',
    className: 'grc-cards',
  },
  {
    headerText: 'Reporting & Analytics',
    controlType: 'Reports',
    className: 'analytics-cards',
  },
];

const GRID_DEFINITION = [{ colspan: 16 }];

/**
 * Converts a string into a URL-friendly slug.
 * @param {string} str - The input string to be converted.
 * @returns {string} A lowercase string with spaces replaced by hyphens.
 */
const slugify = (str: string) => str.replace(/\s/g, '-').toLowerCase();

/**
 * Landing Component
 *
 * This component serves as the main landing page for the Controllership Hub application.
 * It displays the main content area with key capabilities and controllership tasks.
 *
 * @returns {JSX.Element} The rendered Landing component
 */
export default function LandingClassic(): JSX.Element {
  const { data, isLoading, error } = useControls();
  const navigate = useNavigate();
  const controls = data?.controls ?? [];

  const renderCapabilities = () => (
    <div className="capabilities-section">
      <Container>
        <Header variant="h2">Key Capabilities</Header>
        <SpaceBetween size="s">
          {CAPABILITIES.map((capability) => (
            <div key={capability.title} className="capability-box">
              <Icon name={capability.icon} size="medium" variant="normal" />
              <div className="content">
                <h3>{capability.title}</h3>
                <p>{capability.content}</p>
              </div>
            </div>
          ))}
        </SpaceBetween>
      </Container>
    </div>
  );

  const renderCardSections = () => {
    return SECTION_CONFIG.map(({ headerText, controlType, className }) => (
      <ExpandableSection key={headerText} headerText={headerText} defaultExpanded>
        <Box padding={{ vertical: 'xxxs' }}>
          <Grid gridDefinition={GRID_DEFINITION}>
            <Cards
              cardDefinition={{
                header: (item: ControlMetadata) => (
                  <Link
                    href={`/${slugify(item.controlType!)}/${item.controlId}`}
                    onFollow={(e) => {
                      e.preventDefault();
                      navigate(`/${slugify(item.controlType!)}/${item.controlId}`);
                    }}
                  >
                    <h3>{item.controlName}</h3>
                  </Link>
                ),
                sections: [
                  {
                    id: 'description',
                    content: (item: ControlMetadata) => <p>{item.controlSubTitle}</p>,
                  },
                ],
              }}
              items={
                controlType
                  ? controls.filter(
                      (control: ControlMetadata) => control.controlType === controlType
                    )
                  : []
              }
              className={className}
            />
          </Grid>
        </Box>
      </ExpandableSection>
    ));
  };

  return (
    <PageLayout
      defaultPadding={false}
      header={
        <PageHeader
          title="Controllership Hub"
          subtitle="Your one-stop location for consolidated and streamlined controllership work within the WW Amazon Stores Finance."
          description="Finance Controllership Hub brings together the disparate controllership tools, streamlining the once-fragmented world of controllership into a unified and efficient ecosystem. It brings ease, efficiency, and convenience to controllership monitoring, reporting, and task tracking within World Wide Amazon Stores Finance. It is designed to transform how finance controllership processes are managed, monitored, and automated."
        />
      }
    >
      {isLoading ? (
        <Container>
          <Spinner size="large" />
        </Container>
      ) : error ? (
        <Container>
          <Alert type="error">Error loading controls: {error.message}</Alert>
        </Container>
      ) : (
        <Box padding={{ horizontal: 'l', vertical: 'xxxs' }}>
          <Grid gridDefinition={[{ colspan: 9 }, { colspan: 3 }]}>
            <div className="main-content">{renderCardSections()}</div>
            <div className="side-content">{renderCapabilities()}</div>
          </Grid>
        </Box>
      )}
    </PageLayout>
  );
}
